/******* Angular Resourse *******/
import { Inject, Injectable } from '@angular/core';
import { BaseApi } from '@api/base/base.service';
import { HttpClient } from '@angular/common/http';
import { UploadUrlRequestBody, NoticedBackendRequestBody, FileTransferingProgress, FileStage, ShareFileRequestBody, AckFileRequestBody } from '@core/types';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class Files extends BaseApi {

    constructor(@Inject(HttpClient) protected http: HttpClient) {
        super(http);
    }

    /**
     * GET /files - get files list
     * 
     * @param filter
     */
    public getFiles(filter) {
        return this.find("files", filter);
    }

    /**
     * GET /files/{id} - get files list
     * 
     * @param {string} id
     */
    public getFileById(id) {
        return this.findById("files", id);
    }

    /**
     * POST /files/upload-url - get upload url for file
     * 
     * @param {UploadUrlRequestBody} data
     */
    public getUploadUrl(data: UploadUrlRequestBody) {

        let method = "POST",
            url = "files/upload-url",
            routeParams = {},
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PUT {uploadUrl} - upload file directlly to Digital Ocean
     * 
     * @param {string} uploadUrl 
     * @param {File} file 
     * @param {string} contentType 
     * @param {boolean} releasePublic 
     */
    public uploadFile(uploadUrl: string, file: File, contentType: string = "application/json", releasePublic: boolean = false): BehaviorSubject<FileTransferingProgress> {
        const request = new XMLHttpRequest();
        let uploadingProgress = new BehaviorSubject<FileTransferingProgress>({ progress: 0, state: FileStage.IN_PROGRESS })
        request.upload.addEventListener('progress', (e) => {
            let file1Size = file.size;
            if (e.loaded <= file1Size) {
                let percent = Math.round(e.loaded / file1Size * 100);
                uploadingProgress.next({ progress: percent, state: FileStage.IN_PROGRESS });
            }

            if (e.loaded == e.total) {
                uploadingProgress.next({ progress: 100, state: FileStage.IN_PROGRESS });
            }
        });
        request.onload = () => {
            uploadingProgress.complete();
        };
        request.onerror = () => {
            uploadingProgress.next({ progress: 0, state: FileStage.ERROR });
            console.log("Upload File Error...");
        };
        request.ontimeout = () => {
            uploadingProgress.next({ progress: 0, state: FileStage.ERROR });
            console.log("Upload File Timeout...");
        }
        request.open('PUT', uploadUrl);
        request.timeout = 600000;
        request.setRequestHeader("Content-Type", contentType);
        request.setRequestHeader("x-amz-acl", releasePublic ? 'public-read': 'private');
        request.send(file);
        return uploadingProgress;
    }

    /**
     * POST /files - notice backend file got uploaded
     * 
     * @param {NoticedBackendRequestBody} data 
     */
    public uploadFileNoticBackend(data: NoticedBackendRequestBody) {

        let method = "POST",
            url = "files",
            routeParams = {},
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /files/{id}/download - get download url
     * 
     * @param id 
     * @returns 
     */
    public getDownloadUrl(id: string) {
        let short = false;
        let method = "GET",
            url = "files/:id/download",
            routeParams = { id },
            urlParams = { short },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * DELETE /files/{id} - delete the file by id
     * 
     * @param {string} id
     */
    public deleteFile(id: string) {

        let method = "DELETE",
            url = "files/:id",
            routeParams = { id },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * POST /files/{id}/share - share the file by id
     * 
     * @param {ShareFileRequestBody} data 
     */
    public shareFile(id: string, data: ShareFileRequestBody) {

        let method = "POST",
            url = "files/:id/share",
            routeParams = { id },
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PATCH /files/{id}/share/confirm-received - ACK the file by id
     * 
     * @param {string} id
     * @param {AckFileRequestBody} data 
     */
    public confirmReceived(id: string, data: AckFileRequestBody) {

        let method = "PATCH",
            url = "files/:id/share/confirm-received",
            routeParams = { id },
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PUT /files/bulk-update-pci - upload bulk update pci info
     * 
     * @param {any} data form data
     */
    public bulkUpdatePci(data: any) {

        let method = "PUT_PROGRESS",
            url = "files/bulk-update-pci",
            routeParams = {},
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PUT /files/bulk-update-device-info - upload bulk update pci info
     * 
     * @param {any} data form data
     */
    public bulkUpdateDeviceInfo(data: any) {

        let method = "PUT_PROGRESS",
            url = "files/bulk-update-device-info",
            routeParams = {},
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PUT /files/bulk-activate-devices - upload MAC file to bulk activated mqtt user
     * 
     * @param {any} data form data
     */
    public bulkActivate(data: any) {

        let method = "PUT_PROGRESS",
            url = "files/bulk-activate-devices",
            routeParams = {},
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }
}